import React, { useState, useEffect, useContext, useRef } from 'react';
import { Box, Typography, Grid, CircularProgress, IconButton, Tooltip, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { tokens } from '../../theme';
import { TitleScreen, SubTitleScreen, PrimaryButton } from '../../SystemDesign/Styles';
import { useDispatch, useSelector } from 'react-redux';
import SearchInput from '../../SystemDesign/SearchInput';
import FilterSelector from '../../SystemDesign/FilterSelector';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import AddSkill from './AddSkill';
import DeleteSkill from './DeleteSkill';
import UpdateSkill from './UpdateSkill';
import SeeMoreSkill from './SeeMoreSkill';
import SkillCard from '../../SystemDesign/SkillCard';
import { useGetSkillsQuery, useGetUnitsQuery } from '../../services/SkillsManagement';
import { CLEAR_SKILLS_LIST, INITIALIZE_SKILLS } from '../../Reducers/Actions';
import { isEmpty } from '../../Utils/Functions';
import PaginationUI from '../../SystemDesign/PaginationUI';
import LoaderSpinner from '../../SystemDesign/LoaderSpinner';

const SkillsList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const skills = useSelector((state) => state.skills);
  // Also subscribe to unit and discipline reducers to detect changes
  const units = useSelector((state) => state.UnitReducer);
  const disciplines = useSelector((state) => state.DisciplineReducer);
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  
  // State for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState(null);
  
  // State for update dialog
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [skillToUpdate, setSkillToUpdate] = useState(null);
  
  // State for view skill dialog
  const [skillToView, setSkillToView] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  
  // Pagination state - initialize from localStorage if available
  const [paginationInfo, setPaginationInfo] = useState(() => {
    // Check localStorage for saved rowsPerPage value
    const savedRowsPerPage = localStorage.getItem('skills-rowsPerPage');
    const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;
    
    return {
      currentPage: 1,
      rowsPerPage: rowsPerPage,
      total: 0
    };
  });
  
  const [queryParams, setQueryParams] = useState(() => {
    // Check localStorage for saved rowsPerPage value
    const savedRowsPerPage = localStorage.getItem('skills-rowsPerPage');
    const rowsPerPage = savedRowsPerPage !== null ? parseInt(savedRowsPerPage, 10) : 10;
    
    return {
      pagination: JSON.stringify({
        currentPage: 1,
        rowsPerPage: rowsPerPage
      }),
      sorting: "",
      search: "",
      unitId: "", // Ensure this is an empty string, not undefined or null
      priority: "" // Use empty string instead of null for priority
    };
  });
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUnit, setSelectedUnit] = useState("all");
  const [selectedPriority, setSelectedPriority] = useState("all");
  
  // Only make one API call with complete parameters
  const { data: skillsResponse, error, isLoading, isFetching, refetch } = useGetSkillsQuery(queryParams, { 
    // Only refetch when explicitly called, not on mount
    refetchOnMountOrArgChange: false,
    // Don't refetch on focus to prevent duplicate calls
    refetchOnFocus: false,
    // Don't refetch on reconnect to prevent duplicate calls 
    refetchOnReconnect: false,
    // This ensures we don't make the API call until all parameters are ready
    skip: false
  });
  
  // Fetch units for the update dialog - with optimized fetch strategy
  const { data: unitsData, error: unitsError } = useGetUnitsQuery({
    pagination: JSON.stringify({
      currentPage: 1,
      rowsPerPage: 10
    }),
  }, {
    // Only fetch units once, no need to refetch on every mount
    refetchOnMountOrArgChange: false,
    // Keep data in cache longer
    pollingInterval: 0
  });
  
  // Format units data for dropdown
  const unitOptions = React.useMemo(() => {
    // Extract units data with flexible fallbacks
    const units = unitsData?.data?.units || 
                 unitsData?.units || 
                 unitsData?.data?.rows ||
                 unitsData?.rows || 
                 [];
    
    if (!Array.isArray(units) || units.length === 0) {
      return [];
    }
    
    const mappedUnits = units.map((unit, index) => {
      // Extract the unit ID with proper validation
      let unitId;
      if (unit.id !== undefined && unit.id !== null) {
        unitId = String(unit.id);
      } else if (unit.unit_id !== undefined && unit.unit_id !== null) {
        unitId = String(unit.unit_id);
      } else {
        unitId = String(index + 1); // Fallback to index-based ID
      }
      
      // Try to extract name with multiple fallbacks
      const unitName = unit.unit_name || 
                      unit.name || 
                      unit.title || 
                      unit.specific_unit || 
                      (unit.discipline ? `${unit.discipline.name} Unit` : 'Unit ' + unitId);
      
      return {
        value: unitId, // Ensure it's a string for consistent handling
        label: unitName || `Unit ${unitId}`
      };
    });
    
    return mappedUnits;
  }, [unitsData]);
  
  // Priority options (stable)
  const priorityOptions = React.useMemo(() => ([
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ]), []);
  
  // Handle units data errors if needed
  useEffect(() => {
    if (unitsError) setInfoAlert('Error fetching units', 'error');
  }, [unitsError, setInfoAlert]);
  
  // Handle search and filter changes
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };
  
  const handleSearchSubmit = (value) => {
    applyFilters(null, null, null, value);
  };
  
  const handleUnitChange = (value) => {
    if (value === selectedUnit || (value == null && selectedUnit === 'all')) return;
    // Add validation for the value
    if (value === undefined || value === null) {
      value = 'all';
    }
    
    // Set the selected unit in the local state
    setSelectedUnit(value);
    
    // Convert string ID to number for API if not 'all'
    let unitIdValue = '';
    if (value !== 'all') {
      try {
        // Only convert to number if it's a valid numeric string
        if (value && !isNaN(Number(value))) {
          unitIdValue = Number(value);
        }
      } catch (error) {
        // Silently handle errors
      }
    }
    
    // Use direct API call parameters
    const newParams = { ...queryParams };
    newParams.unitId = value === 'all' ? '' : unitIdValue;
    newParams.pagination = JSON.stringify({
      currentPage: 1, // Reset to first page
      rowsPerPage: JSON.parse(queryParams.pagination).rowsPerPage
    });
    
    const prevStr = JSON.stringify(queryParams);
    const nextStr = JSON.stringify(newParams);
    if (prevStr !== nextStr) {
      setQueryParams(newParams);
    }
    // We'll let the useEffect trigger the refetch
  };
  
  const handlePriorityChange = (value) => {
    if (value === selectedPriority || (value == null && selectedPriority === 'all')) return;
    setSelectedPriority(value);
    
    // When 'all' is selected, we'll use an empty string instead of null
    // This ensures the backend treats it as 'get all priorities' instead of 'no priority'
    applyFilters({ priority: value === 'all' ? '' : value });
  };
  
  // Extract skills from the API response with fallbacks
  const apiSkills = skillsResponse?.data?.skills || 
                 skillsResponse?.skills || 
                 skillsResponse?.rows || 
                 (Array.isArray(skillsResponse) ? skillsResponse : []);
                 
  // Extract pagination info from the API response
  const apiPagination = skillsResponse?.data?.pagination || skillsResponse?.pagination;

  // Memoize normalized pagination info to avoid changing object identity on every render
  const normalizedPaginationInfo = React.useMemo(() => {
    const current = apiPagination?.page ?? apiPagination?.currentPage ?? parseInt(JSON.parse(queryParams.pagination || '{}').currentPage || 1, 10);
    const limit = apiPagination?.limit ?? apiPagination?.rowsPerPage ?? parseInt(JSON.parse(queryParams.pagination || '{}').rowsPerPage || 10, 10);
    const total = apiPagination?.total ?? apiSkills.length ?? 0;
    return { currentPage: current, rowsPerPage: limit, total };
  }, [apiPagination, queryParams.pagination, apiSkills.length]);

  // Update local state when API pagination info changes (only when values actually differ)
  useEffect(() => {
    if (!apiPagination) return;
    setPaginationInfo((prev) => {
      const next = normalizedPaginationInfo;
      if (
        prev.currentPage === next.currentPage &&
        prev.rowsPerPage === next.rowsPerPage &&
        prev.total === next.total
      ) {
        return prev; // no change -> avoid re-render loop
      }
      return next;
    });
  }, [apiPagination, normalizedPaginationInfo.currentPage, normalizedPaginationInfo.rowsPerPage, normalizedPaginationInfo.total]);

  // Initialize skills in Redux store (stabilized)
  const isInitialRender = useRef(true);
  const lastQueryParams = useRef(null);
  const lastProcessedSkillsKeyRef = useRef(null);
  
  // Handle API responses and update the Redux store
  useEffect(() => {
    if (!skillsResponse) return;
    const shouldInitFromAPI = Array.isArray(apiSkills) && !error;
    if (!shouldInitFromAPI) return;

    // Build a stable key from the data to detect real changes
    const key = (() => {
      try {
        return JSON.stringify(apiSkills.map((s) => s?.id ?? s?.skill_id ?? s?.skill_code ?? s));
      } catch {
        return String(apiSkills?.length ?? 0);
      }
    })();

    if (key === lastProcessedSkillsKeyRef.current) return;
    lastProcessedSkillsKeyRef.current = key;

    // Update store with new data
    dispatch({ type: CLEAR_SKILLS_LIST });
    dispatch({ type: INITIALIZE_SKILLS, payload: apiSkills });
  }, [apiSkills, dispatch, error, skillsResponse]);
  
  // Effect to refetch skills when units or disciplines change (avoid loops)
  useEffect(() => {
    if (isInitialRender.current) return;
    refetch();
  }, [units, disciplines, refetch]);

  // Use a single consolidated effect for API calls to avoid duplicates
  // This must remain in the same position to maintain hook order
  useEffect(() => {
    // Skip the initial render to prevent double API calls on component mount
    if (isInitialRender.current) {
      isInitialRender.current = false;
      // Store initial params
      lastQueryParams.current = JSON.stringify(queryParams);
      return;
    }
    
    // Convert current params to string for comparison
    const currentParamsString = JSON.stringify(queryParams);
    
    // Compare with previous params to prevent duplicate calls
    if (currentParamsString === lastQueryParams.current) {
      return;
    }
    
    // Update last params reference
    lastQueryParams.current = currentParamsString;
    
    // Use a debounce to ensure all filters are applied before making the API call
    const timeoutId = setTimeout(() => {
      refetch();
    }, 300); // Delay to ensure all filter changes are batched
    
    return () => clearTimeout(timeoutId);
  }, [queryParams, refetch]);

  // Handle edit skill
  const handleEditSkill = (skill) => {
    setSkillToUpdate(skill);
    setUpdateDialogOpen(true);
  };

  // Handle view skill details
  const handleViewSkill = (skill) => {
    setSkillToView(skill);
    setViewDialogOpen(true);
  };
  
  // Handle closing view dialog
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
  };
  
  // Handler for delete icon click
  const handleDeleteClick = (skill) => {
    setSkillToDelete(skill);
    setDeleteDialogOpen(true);
  };
  
  // Handler for successful deletion
  const handleSkillDeleted = () => {
    // Do not auto-refetch; list will update via Redux or on manual refresh
  };
  
  // Handler for successful update
  const handleSkillUpdated = () => {
    // Do not auto-refetch; list will update via Redux or on manual refresh
  };

  // Handle error from API
  useEffect(() => {
    if (error) {
      setInfoAlert({ 
        severity: 'error', 
        message: error.data?.detail || 'Failed to fetch skills' 
      });
      setOpenSnackBar(true);
    }
  }, [error, setInfoAlert, setOpenSnackBar]);

  // Apply filters to update query params (similar to DisciplineList)
  const applyFilters = (filters = null, paginationInfo = null, sortingInfo = null, searchValue = null) => {
    // Start with current params
    const newParams = { ...queryParams };
    
    // console.log('Current query params:', queryParams);
    // console.log('Filters to apply:', filters);

    // Apply filters if provided
    if (filters !== null) {
      if ('unitId' in filters) {
        // Extra defensive check to prevent NaN values
        try {
          if (filters.unitId === '' || filters.unitId === null || filters.unitId === undefined || filters.unitId === 'all' || isNaN(Number(filters.unitId))) {
            newParams.unitId = '';
          } else {
            const numValue = Number(filters.unitId);
            newParams.unitId = isNaN(numValue) ? '' : numValue;
          }
        } catch (error) {
          console.error('Error processing unitId:', error);
          newParams.unitId = '';
        }
        // console.log(`Setting unitId in params to: "${newParams.unitId}"`, typeof newParams.unitId);
      }
      if (filters.priority !== undefined) {
        newParams.priority = filters.priority;
        // console.log(`Setting priority in params to: "${filters.priority}"`);
      }
    }
    
    // Apply pagination if provided
    if (paginationInfo !== null) {
      // Get current pagination
      const currentPagination = newParams.pagination 
        ? JSON.parse(newParams.pagination) 
        : { currentPage: 1, rowsPerPage: 10 };
      
      // Merge with new pagination info
      const updatedPagination = { ...currentPagination, ...paginationInfo };
      
      // Save rowsPerPage to localStorage
      if (paginationInfo.rowsPerPage) {
        localStorage.setItem('skills-rowsPerPage', paginationInfo.rowsPerPage.toString());
      }
      
      // Update pagination in params
      newParams.pagination = JSON.stringify(updatedPagination);
    }
    
    // Apply sorting if provided
    if (sortingInfo !== null) {
      newParams.sorting = sortingInfo;
    }
    
    // Apply search if provided
    if (searchValue !== null) {
      newParams.search = searchValue;
    }
    
    const prevStr = JSON.stringify(queryParams);
    const nextStr = JSON.stringify(newParams);
    if (prevStr !== nextStr) {
      // console.log('New query params to be applied:', newParams);
      setQueryParams(newParams);
    }
  };

  // Handle page change - simplified to use applyFilters
  const handlePageChange = (newPage, rowsPerPage) => {
    applyFilters(null, { currentPage: newPage, rowsPerPage });
  };

  // Handle rows per page change - simplified to use applyFilters
  const handleRowsPerPageChange = (newRowsPerPage) => {
    applyFilters(null, { currentPage: 1, rowsPerPage: newRowsPerPage });
  };

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography sx={TitleScreen(colors)}>
            Skills
          </Typography>
          <Typography sx={SubTitleScreen(colors)}>
            Manage skills and competencies
          </Typography>
        </Box>
        <Box>
          <AddSkill />
        </Box>
      </Box>
      
      {/* Search and Filter Section */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
          <SearchInput 
            placeholder="Search by skill name, skill code or description..." 
            onSearch={handleSearchSubmit}
            sx={{ flexGrow: 1 }}
          />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FilterSelector
            value={selectedUnit}
            onChange={handleUnitChange}
            options={unitOptions}
            defaultLabel="all units"
            valueKey="value"
            labelKey="label"
            data-testid="unit-filter"
            sx={{ minWidth: { xs: '100%', sm: '160px' } }}
          />
          <FilterSelector
            value={selectedPriority}
            onChange={handlePriorityChange}
            options={priorityOptions}
            defaultLabel="all priorities"
            valueKey="value"
            labelKey="label"
            sx={{ minWidth: { xs: '100%', sm: '160px' } }}
          />
        </Stack>
      </Box>
      
      {/* Global loading overlay (match EmployeesList behavior) */}
      <LoaderSpinner loading={isLoading} />

        <div style={{ marginTop: "1rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "24px" }}>
            {skills && skills.length > 0 ? (
              skills.map((skill, index) => (
                <div key={skill.id || skill.skill_id || index}>
                  <SkillCard
                    skillId={skill.id || skill.skill_id}
                    skillCode={skill.skill_code}
                    name={skill.name}
                    description={skill.description}
                    priority={skill.priority}
                    unitCode={skill.unit?.specific_unit}
                    discipline={skill.unit?.discipline?.name}
                    disciplineColor={skill.unit?.discipline?.color}
                    disciplineIcon={skill.unit?.discipline?.icon}
                    internalRefValue={skill.internal_reference}
                    externalRefValue={skill.external_reference}
                    skillLevels={[
                      'Awareness',
                      'Application',
                      'Management'
                    ]}
                    skillLevelDescriptions={skill.skillLevelDescriptions}
                    onView={() => handleViewSkill(skill)}
                    onEdit={() => handleEditSkill(skill)}
                    onDelete={() => handleDeleteClick({...skill, id: parseInt(skill.id || skill.skill_id, 10)})}
                  />
                </div>
              ))
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, gridColumn: '1 / -1' }}>
                <Typography variant="body1" color="text.secondary">
                  No skills found. Add a new skill to get started.
                </Typography>
              </Box>
            )}
          </div>
        </div>
      
      {/* Pagination */}
      <PaginationUI
        paginationInfo={normalizedPaginationInfo}
        isLoading={isLoading || isFetching}
        onPageChange={(page, rowsPerPage) => applyFilters(
          null, 
          { currentPage: page, rowsPerPage }
        )}
        onRowsPerPageChange={(rowsPerPage) => applyFilters(
          null,
          { currentPage: 1, rowsPerPage }
        )}
      />
      
      {/* Delete Skill Dialog */}
      <DeleteSkill
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        skill={skillToDelete}
        onDeleted={handleSkillDeleted}
      />
      
      {/* Update Skill Dialog */}
      <UpdateSkill
        open={updateDialogOpen}
        onClose={() => setUpdateDialogOpen(false)}
        skill={skillToUpdate}
        units={unitsData?.data?.units || unitsData?.units || unitsData?.rows || []}
        onSubmit={handleSkillUpdated}
      />

      {/* View Skill Details */}
      {skillToView && (
        <SeeMoreSkill 
          skill={skillToView} 
          isOpen={viewDialogOpen}
          onClose={handleCloseViewDialog}
        />
      )}
    </Box>
  );
};

export default SkillsList;